import { get } from "lodash";
import { subscribe } from "pusher-redux";
import { call, all, takeLatest, put } from "redux-saga/effects";

import { AT_TYPES } from "../../constants/entity.js";
import { CONNECTIONS } from "../actions/actionConstants.js";
import { OPERATIONS } from "../../constants/operations.js";
import { PUSHER } from "../../constants/pusher.js";
import { ROW_STATE } from "../../components/forms/formConstants.js";

import { createAction } from "../actions/actionUtils.js";
import { apiReset } from "../reducers/api/apiActions.js";
import { operationAction } from "../reducers/operations/operationActions.js";
import { getProjectConnections } from "../reducers/projects/projectsActions.js";

import { getEntitySaga } from "./apiSagas.js";
import { operationSaga } from "./operationSagas.js";
import { selectEndpointByTypeSaga } from "./endpointSagas.js";

// -- HELPERS -- //

// Need a better/nicer way of sanitising and extracting this
function buildPayload(connection, fieldPath) {
  const { description, value, scope } = connection;
  return { description, value, scope, fieldPath };
}

// -- CONNECTION SAGAS -- //

function* createConnectionSaga(projectId, payload) {
  try {
    payload.project = projectId;

    yield call(
      operationSaga,
      operationAction(
        CONNECTIONS,
        yield call(selectEndpointByTypeSaga, AT_TYPES.CONNECTION),
        OPERATIONS.CREATE,
        payload
      ),
      true
    );
  } catch (error) {
    console.log("createConnectionSaga :: error ::", error);
    throw error;
  }
}

function* connectionOperationSaga(connection, operationName, payload) {
  try {
    yield call(
      operationSaga,
      operationAction(CONNECTIONS, connection, operationName, payload),
      true
    );
  } catch (error) {
    console.log("connectionOperationSaga :: error ::", operationName, error);
    throw error;
  }
}

function* noneBlockingAll(calls) {
  const errors = [];

  for (let i = 0; i < calls.length; i++) {
    try {
      yield calls[i];
    } catch (error) {
      // console.log("noneBlockingAll error:", error);
      errors.push(error);
    }
  }

  if (errors.length > 0) {
    throw errors;
  }
}

export function* bulkConnectionsSaga(action) {
  const { connections, projectId } = action.payload;

  let calls = {
    edit: [],
    new: [],
    delete: []
  };

  for (let i = 0; i < connections.length; i++) {
    const connection = connections[i];

    const payload = buildPayload(connection, `connections[${i}]`);

    switch (connection.state) {
      case ROW_STATE.EDIT:
        calls.edit.push(
          call(connectionOperationSaga, connection, OPERATIONS.UPDATE, payload)
        );
        break;
      case ROW_STATE.NEW:
        calls.new.push(call(createConnectionSaga, projectId, payload));
        break;
      case ROW_STATE.DELETE:
        calls.delete.push(
          call(connectionOperationSaga, connection, OPERATIONS.DELETE, payload)
        );
        break;
    }
  }

  // make sure to perform the delete(s) before the edit and adds
  // (preventing "duplicate value" errors from the API in changes that are a delete
  // and addition of the same value/scope)
  try {
    yield all(calls.delete);
  } catch (error) {
    throw error;
  }

  try {
    yield noneBlockingAll(calls.edit);
  } catch (error) {
    throw error;
  }

  try {
    yield noneBlockingAll(calls.new);
  } catch (error) {
    throw error;
  }
}

// Base Sagas

export function* getConnectionsSaga(action) {
  // console.log("getConnectionsSaga:", action);
  yield put(createAction(CONNECTIONS.RESET));

  const result = yield call(
    getEntitySaga,
    CONNECTIONS,
    { showErrorModal: false, autoComplete: false },
    action
  );

  //  subscribe to pusher channels for connections
  const connections = get(result, `hydra:member`, []);

  if (connections.length > 0) {
    for (let i = 0; i < connections.length; i++) {
      subscribe(
        `private-${connections[i].id}`,
        PUSHER.EVENTS.UPDATE,
        CONNECTIONS.PUSHER_UPDATE
      );
    }
  }

  yield put(createAction(CONNECTIONS.GET_COMPLETE));
}

function* updateConnectionsSaga(action) {
  // console.log("updateConnections:", action);
  const { connections } = action.payload;
  const projectId = get(action, "payload.@id", null);
  const searchParams = get(action, "payload.searchParams", undefined);

  try {
    yield call(bulkConnectionsSaga, {
      payload: { projectId, connections }
    });
    // GET the project and clear the api result logs once all updates are complete
    // it's cleaner than trying to update the reducer with all of the various returns that happen here
    yield put(apiReset());
    yield put(getProjectConnections(projectId, searchParams));
  } catch (error) {
    console.log("updateConnectionsSaga :: error ::", error);
    // yield cancel();
  }
  // whatever happens we have to complete the project put or the SPA will hang
  yield put(createAction(CONNECTIONS.PUT_COMPLETE));
}

// -- WATCHERS -- //

export function* watchConnectionSagas() {
  yield takeLatest(CONNECTIONS.GET, getConnectionsSaga);
  yield takeLatest(CONNECTIONS.PUT, updateConnectionsSaga);
}
